import React from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Home, Layers, Users, BookOpen, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
export function DashboardEmptyPage() {
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const quickStartSteps = [{
    icon: Home,
    title: 'Add Your First Community',
    description: 'Set up communities and floor plans for your buyers',
    action: 'Add Community',
    path: `/${builderId}/communities`,
    color: 'bg-blue-50 text-blue-600'
  }, {
    icon: Layers,
    title: 'Create Option Packages',
    description: 'Build curated upgrade packages for different rooms',
    action: 'Create Package',
    path: `/${builderId}/packages`,
    color: 'bg-purple-50 text-purple-600'
  }, {
    icon: Users,
    title: 'Invite Your Team',
    description: 'Add team members and configure permissions',
    action: 'Manage Team',
    path: `/${builderId}/settings/general`,
    color: 'bg-emerald-50 text-emerald-600'
  }];
  return <div className="space-y-8 max-w-6xl mx-auto" data-id="element-4736">
      {/* Header */}
      <div className="text-center pt-8" data-id="element-4737">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 mb-4" data-id="element-4738">
          <Sparkles className="h-8 w-8 text-primary-600" data-id="element-4739" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-4740">
          Welcome to ResVR
        </h1>
        <p className="text-lg text-slate-500 max-w-2xl mx-auto" data-id="element-4741">
          Let's get your virtual reality home tours set up. Follow these steps
          to start showcasing your homes to buyers.
        </p>
      </div>

      {/* Quick Start Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4742">
        {quickStartSteps.map((step, index) => {
        const Icon = step.icon;
        return <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(step.path)} data-id="element-4743">
              <CardContent className="p-6" data-id="element-4744">
                <div className={`w-12 h-12 rounded-lg ${step.color} flex items-center justify-center mb-4`} data-id="element-4745">
                  <Icon className="h-6 w-6" data-id="element-4746" />
                </div>
                <div className="flex items-start justify-between mb-2" data-id="element-4747">
                  <h3 className="text-lg font-semibold text-slate-900" data-id="element-4748">
                    {step.title}
                  </h3>
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded" data-id="element-4749">
                    {index + 1}/3
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-4" data-id="element-4750">
                  {step.description}
                </p>
                <Button variant="outline" size="sm" className="w-full group-hover:bg-slate-50" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-4752" />} data-id="element-4751">
                  {step.action}
                </Button>
              </CardContent>
            </Card>;
      })}
      </div>

      {/* What You Can Do Section */}
      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200" data-id="element-4753">
        <CardContent className="p-8" data-id="element-4754">
          <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-4755">
            What you can do with ResVR
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4756">
            <div className="flex gap-4" data-id="element-4757">
              <div className="flex-shrink-0" data-id="element-4758">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-4759">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" data-id="element-4760" />
                </div>
              </div>
              <div data-id="element-4761">
                <h3 className="font-semibold text-slate-900 mb-1" data-id="element-4762">
                  Virtual Reality Tours
                </h3>
                <p className="text-sm text-slate-600" data-id="element-4763">
                  Let buyers explore homes in immersive VR before they're built
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-4764">
              <div className="flex-shrink-0" data-id="element-4765">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-4766">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" data-id="element-4767" />
                </div>
              </div>
              <div data-id="element-4768">
                <h3 className="font-semibold text-slate-900 mb-1" data-id="element-4769">
                  Option Management
                </h3>
                <p className="text-sm text-slate-600" data-id="element-4770">
                  Create and manage upgrade packages with real-time pricing
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-4771">
              <div className="flex-shrink-0" data-id="element-4772">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-4773">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" data-id="element-4774" />
                </div>
              </div>
              <div data-id="element-4775">
                <h3 className="font-semibold text-slate-900 mb-1" data-id="element-4776">
                  Buyer Analytics
                </h3>
                <p className="text-sm text-slate-600" data-id="element-4777">
                  Track engagement, time spent, and popular upgrade choices
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-4778">
              <div className="flex-shrink-0" data-id="element-4779">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-4780">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" data-id="element-4781" />
                </div>
              </div>
              <div data-id="element-4782">
                <h3 className="font-semibold text-slate-900 mb-1" data-id="element-4783">
                  Selection Tracking
                </h3>
                <p className="text-sm text-slate-600" data-id="element-4784">
                  Capture buyer selections and generate detailed summaries
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4785">
        <Card className="bg-slate-900 text-white border-slate-800" data-id="element-4786">
          <CardContent className="p-6" data-id="element-4787">
            <BookOpen className="h-8 w-8 mb-3 text-primary-400" data-id="element-4788" />
            <h3 className="font-bold text-lg mb-2" data-id="element-4789">Learning Center</h3>
            <p className="text-slate-400 text-sm mb-4" data-id="element-4790">
              Watch tutorials and read guides to get the most out of ResVR
            </p>
            <Button variant="secondary" className="w-full !bg-white !text-slate-900 hover:!bg-slate-100" onClick={() => navigate(`/${builderId}/learn`)} data-id="element-4791">
              Browse Resources
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-primary-50 border-primary-200" data-id="element-4792">
          <CardContent className="p-6" data-id="element-4793">
            <Users className="h-8 w-8 mb-3 text-primary-600" data-id="element-4794" />
            <h3 className="font-bold text-lg mb-2 text-slate-900" data-id="element-4795">
              Need Help?
            </h3>
            <p className="text-slate-600 text-sm mb-4" data-id="element-4796">
              Our support team is ready to help you get started
            </p>
            <Button variant="primary" className="w-full" onClick={() => navigate(`/${builderId}/support`)} data-id="element-4797">
              Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Stats Preview (Empty State) */}
      <Card data-id="element-4798">
        <CardContent className="p-8" data-id="element-4799">
          <div className="text-center" data-id="element-4800">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4" data-id="element-4801">
              <Layers className="h-8 w-8 text-slate-400" data-id="element-4802" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-4803">
              Your Dashboard Will Appear Here
            </h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto" data-id="element-4804">
              Once you add communities and buyers start exploring, you'll see
              real-time analytics, engagement metrics, and popular upgrade
              choices right here.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}